import React, { memo } from 'react';
import { Button } from '@cuemath/web-comps';

import DateRangeFilter from 'comps/date-range-filter';
import FilterDropdown from 'comps/filter/new-filter-dropdown';

import CountrySelect from '../country-select';
import { FiltersBar, FilterField, ActionsField, FieldLabelText } from '../../styled-comps';
import {
  REGION_OPTIONS,
  USER_TYPE_OPTIONS,
  CHANNEL_OPTIONS,
  SUB_SOURCE_OPTIONS,
  COMPARE_OPTIONS,
  COMPARE_MODES,
} from '../../constants';

const Label = ({ children }) => (
  <FieldLabelText variant="eyebrow2">{children}</FieldLabelText>
);

// Summary filter bar. `filters` is the DRAFT state owned by the screen;
// `onChange(patch)` edits the draft — queries fire only when the user presses
// Apply (onApply). Date/compare/region/user-type are always shown; category and
// sub-source come from the show* flags. All list filters are multi-select.
// (Page and experiment have their own dedicated tabs, so they are not here.)
const Filters = memo(
  ({ filters, onChange, onApply, onReset, hasUnappliedChanges, showCategory, showSubSource }) => {
    const setRange = range =>
      onChange({
        startDate: range ? range.startDate : null,
        endDate: range ? range.endDate : null,
      });

    const setCompareRange = range =>
      onChange({
        compareStart: range ? range.startDate : null,
        compareEnd: range ? range.endDate : null,
      });

    return (
      <FiltersBar>
        <FilterField $width="260px">
          <Label>Date range</Label>
          <DateRangeFilter
            value={{ startDate: filters.startDate, endDate: filters.endDate }}
            onDateRangeChange={setRange}
          />
        </FilterField>

        <FilterField $width="180px">
          <Label>Compare with</Label>
          <FilterDropdown
            options_list={COMPARE_OPTIONS}
            filter={{ value: filters.compareMode }}
            onChange={value => onChange({ compareMode: value || COMPARE_MODES.NONE })}
          />
        </FilterField>

        {filters.compareMode === COMPARE_MODES.CUSTOM && (
          <FilterField $width="260px">
            <Label>Comparison range</Label>
            <DateRangeFilter
              value={{ startDate: filters.compareStart, endDate: filters.compareEnd }}
              onDateRangeChange={setCompareRange}
            />
          </FilterField>
        )}

        <FilterField $width="180px">
          <Label>Region</Label>
          <FilterDropdown
            options_list={REGION_OPTIONS}
            filter={{ value: filters.regions }}
            onChange={value => onChange({ regions: value || [] })}
            multiple
          />
        </FilterField>

        <CountrySelect
          regions={filters.regions}
          value={filters.countries}
          onChange={countries => onChange({ countries })}
          width="220px"
        />

        <FilterField $width="190px">
          <Label>User type</Label>
          <FilterDropdown
            options_list={USER_TYPE_OPTIONS}
            filter={{ value: filters.userTypes }}
            onChange={value => onChange({ userTypes: value || [] })}
            multiple
          />
        </FilterField>

        {showCategory && (
          <FilterField $width="210px">
            <Label>Category</Label>
            <FilterDropdown
              options_list={CHANNEL_OPTIONS}
              filter={{ value: filters.channels }}
              onChange={value => onChange({ channels: value || [] })}
              multiple
            />
          </FilterField>
        )}

        {showSubSource && (
          <FilterField $width="230px">
            <Label>Sub-source</Label>
            <FilterDropdown
              options_list={SUB_SOURCE_OPTIONS}
              filter={{ value: filters.subSources }}
              onChange={value => onChange({ subSources: value || [] })}
              multiple
            />
          </FilterField>
        )}

        <ActionsField>
          <Button
            size="small"
            label={hasUnappliedChanges ? 'Apply *' : 'Apply'}
            onClick={onApply}
          />
          <Button
            size="small"
            variant="tertiaryWithUnderline"
            label="Reset"
            onClick={onReset}
          />
        </ActionsField>
      </FiltersBar>
    );
  },
);

export default Filters;
